<template>
  <div>
  <HeaderComponent />
    <main class="pt-[70px] w-full h-screen bg-gray-100 relative flex">
      <SideBarComponent />
        <div class="lg:w-[calc(100%-250px)] md:w-[calc(100%-78px)] lg:left-[250px] md:left-[78px] relative top-0 overflow-y-auto overflow-x-hidden h-full md:p-[25px] p-[20px]">
        <!-- Header avec bouton d'ajout -->
        <section class="w-full duration-500 relative flex flex-col flex-wrap items-center justify-between mb-[40px]">
          <div class="flex justify-between items-center mb-6 w-full">
            <h1 class="text-2xl font-bold text-gray-800">Gestion des biens immobiliers</h1>
            <button 
              v-if="authStore.canManageApartments"
              @click="goToAddBienPage()"
              class="bg-[#D4AF37] hover:bg-[#C9A227] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
              </svg>
              Nouveau bien
            </button>
          </div>

          <!-- Filtres -->
          <div class="bg-white p-4 rounded-lg shadow mb-6 w-full">
            <div class="mb-3">
              <label class="block text-sm font-medium text-gray-700 mb-1">Recherche par nom / mot-clé</label>
              <input
                v-model="filters.keyword"
                @input="debouncedFetch"
                type="text"
                placeholder="Rechercher un bien par nom..."
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              >
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  v-model="filters.type"
                  @change="fetchApartments"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
                  <option value="">Tous les types</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Studio">Studio</option>
                  <option value="Maison">Maison</option>
                  <option value="Villa">Villa</option>
                </select>
              </div>
              <div class="md:block hidden">
                <label class="block text-sm font-medium text-gray-700 mb-1">Localité</label>
                <input
                  v-model="filters.locality"
                  @input="debouncedFetch"
                  type="text"
                  placeholder="Rechercher par ville"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
              </div>
              <div class="md:block hidden">
                <label class="block text-sm font-medium text-gray-700 mb-1">Budget max</label>
                <input
                  v-model="filters.budget"
                  @input="debouncedFetch"
                  type="number"
                  placeholder="Prix maximum"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
              </div>
              <div class="md:block hidden">
                <label class="block text-sm font-medium text-gray-700 mb-1">Chambres</label>
                <select
                  v-model="filters.rooms"
                  @change="fetchApartments"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
                  <option value="">Toutes</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3+</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Liste des appartements -->
          <div class="w-full">
            <div v-if="apartments.length > 0" class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6 items-stretch">
              <div
                v-for="apartment in apartments"
                :key="apartment._id"
                class="h-full min-h-[390px] rounded-t-[26px] relative overflow-hidden duration-300 flex flex-col"
              >
                <button
                  @click="shareApartment(apartment)"
                  class="w-[30px] h-[30px] rounded-full absolute top-[10px] right-[10px] z-10 flex items-center justify-center text-white bg-black/50 hover:bg-black/70 transition-colors duration-200 group"
                  title="Partager ce bien"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" class="group-hover:scale-110 transition-transform">
                    <circle cx="18" cy="5" r="1" fill="currentColor" opacity=".3"/>
                    <circle cx="6" cy="12" r="1" fill="currentColor" opacity=".3"/>
                    <circle cx="18" cy="19.02" r="1" fill="currentColor" opacity=".3"/>
                    <path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81c1.66 0 3-1.34 3-3s-1.34-3-3-3s-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65c0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92M18 4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1M6 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1m12 7.02c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1"/>
                  </svg>
                </button>
                <div class="relative h-[220px] flex-none rounded-[26px] overflow-hidden">
                  <img
                    :src="getApartmentImage(apartment)"
                    :alt="apartment.title"
                    class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  >
                  <div v-if="apartment?.category === 'achat'" class="absolute top-[10px] left-[10px] bg-[#1f2937] text-white text-xs font-bold px-2 py-2 rounded-[16px]">
                    En vente
                  </div>
                  <div v-else class="absolute top-[10px] left-[10px] bg-[#D4AF37] text-white text-xs font-bold px-2 py-2 rounded-[16px]">
                    À Réserver
                  </div>
                  <div v-if="isFurnished(apartment)" class="absolute top-[50px] left-[10px] bg-green-600 text-white text-xs font-bold px-2 py-2 rounded-[16px] flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3s1.34 3 3 3m12-6h-8v7H3V7H1v10h22v-6c0-2.21-1.79-4-4-4"/>
                    </svg>
                    Meublé
                  </div>
                  <div v-else class="absolute top-[50px] left-[10px] bg-gray-600 text-white text-xs font-bold px-2 py-2 rounded-[16px] flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8"/>
                    </svg>
                    Non meublé
                  </div>
                </div>

                <div class="py-2 flex flex-1 flex-col">
                  <router-link
                    :to="`/apartments/${apartment._id}`"
                    class="min-h-[58px] text-sm font-semibold cursor-pointer hover:underline duration-200 text-gray-800 mb-2 flex flex-wrap items-start content-start"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" class="mr-2">
                      <path fill="currentColor" fill-rule="evenodd" d="M13.106 22h-2.212c-3.447 0-5.17 0-6.345-1.012s-1.419-2.705-1.906-6.093l-.279-1.937c-.38-2.637-.57-3.956-.029-5.083s1.691-1.813 3.992-3.183l1.385-.825C9.8 2.622 10.846 2 12 2s2.199.622 4.288 1.867l1.385.825c2.3 1.37 3.451 2.056 3.992 3.183s.35 2.446-.03 5.083l-.278 1.937c-.487 3.388-.731 5.081-1.906 6.093S16.553 22 13.106 22m-4.708-6.447a.75.75 0 0 1 1.049-.156c.728.54 1.607.853 2.553.853s1.825-.313 2.553-.853a.75.75 0 1 1 .894 1.205A5.77 5.77 0 0 1 12 17.75a5.77 5.77 0 0 1-3.447-1.148a.75.75 0 0 1-.155-1.049" clip-rule="evenodd"/>
                    </svg>
                    {{ apartment.title }}
                    <span class="text-xs w-full text-gray-500">
                      {{ apartment.locality }} - {{ apartment.rooms || 'N/A' }} Chambres
                    </span>
                  </router-link>

                  <div class="flex items-center">
                    <span class="flex flex-wrap text-xs items-center">
                      <span class="text-xs font-bold flex items-center text-[#1f2937]">
                        {{ formatPrice(apartment.price) }} FCFA<span v-if="apartment?.category !== 'achat'" class="text-sm">/nuitée</span>
                      </span>
                    </span>
                  </div>

                  <div class="mt-auto pt-3 border-t border-gray-200">
                    <div class="flex items-center justify-between gap-2 mb-2">
                      <button 
                        @click="toggleAvailability(apartment)"
                        :disabled="!authStore.canManageApartments"
                        :class="{
                          'bg-green-100 text-green-800': apartment.availability === 'available',
                          'bg-yellow-100 text-yellow-800': apartment.availability === 'coming_soon',
                          'bg-red-100 text-red-800': apartment.availability === 'unavailable',
                          'cursor-not-allowed opacity-70': !authStore.canManageApartments
                        }"
                        class="px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors"
                      >
                        {{ labelDisponibilite(apartment.availability) }}
                      </button>
                      <span class="text-[11px] text-gray-400">
                        #{{ apartment._id.toString().slice(-6).toUpperCase() }}
                      </span>
                    </div>

                    <template v-if="authStore.canViewOwnerInfo">
                      <p class="text-xs text-gray-500 font-medium">Prix propriétaire :</p>
                      <p class="text-xs text-gray-600">
                        <span class="font-semibold text-gray-800">{{ apartment.ownerPriceNight ? formatPrice(apartment.ownerPriceNight) + ' CFA' : '—' }}</span>
                        <span class="text-gray-400"> / nuitée</span>
                        &nbsp;·&nbsp;
                        <span class="font-semibold text-gray-800">{{ apartment.ownerPriceMonth ? formatPrice(apartment.ownerPriceMonth) + ' CFA' : '—' }}</span>
                        <span class="text-gray-400"> / mois</span>
                      </p>
                      <button
                        v-if="apartment.ownerPhone"
                        @click.stop="openContactPopup(apartment)"
                        class="text-xs text-[#D4AF37] hover:text-[#C9A227] underline underline-offset-2 font-medium"
                        type="button"
                      >
                        {{ apartment.ownerPhone }}
                      </button>
                      <p v-else class="text-xs text-gray-500">Contact: Non renseigné</p>
                    </template>
                    <p v-else class="text-xs text-gray-500">
                      Infos propriétaire réservées au service client
                    </p>

                    <div class="flex items-center gap-2 mt-3">
                      <button 
                        v-if="authStore.canManageApartments"
                        @click="editApartment(apartment)"
                        class="flex-1 h-[34px] rounded-md bg-white border border-gray-300 text-gray-700 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center"
                        title="Modifier"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        v-if="authStore.isSuperAdmin"
                        @click="deleteApartment(apartment._id)"
                        class="flex-1 h-[34px] rounded-md bg-white border border-gray-300 text-gray-700 hover:border-red-500 hover:text-red-500 transition-colors flex items-center justify-center"
                        title="Supprimer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Aucun résultat -->
            <div v-else class="p-8 text-center text-gray-500">
              Aucun bien immobilier trouvé avec ces critères
            </div>
          </div>

          <!-- Pagination -->
          <div class="mt-6 flex items-center justify-between w-full">
            <button 
              @click="prevPage"
              :disabled="currentPage === 1"
              :class="{
                'text-gray-400 cursor-not-allowed': currentPage === 1,
                'text-gray-700 hover:text-[#D4AF37]': currentPage > 1
              }"
              class="flex items-center gap-1 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              Précédent
            </button>

            <div class="flex items-center gap-2">
              <button 
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="{
                  'bg-[#D4AF37] text-white': page === currentPage,
                  'bg-white text-gray-700 hover:bg-gray-100': page !== currentPage
                }"
                class="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
              >
                {{ page === '...' ? '...' : page }}
              </button>
            </div>

            <button 
              @click="nextPage"
              :disabled="currentPage === totalPages"
              :class="{
                'text-gray-400 cursor-not-allowed': currentPage === totalPages,
                'text-gray-700 hover:text-[#D4AF37]': currentPage < totalPages
              }"
              class="flex items-center gap-1 transition-colors"
            >
              Suivant
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </section>
        </div>

    <!-- Popup contact propriétaire -->
    <div
      v-if="isContactPopupOpen"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      @click="closeContactPopup"
    >
      <div
        class="bg-white rounded-xl shadow-xl w-full max-w-sm p-5"
        @click.stop
      >
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Contacter le propriétaire</h3>
            <p class="text-sm text-gray-600">
              <span class="font-medium">{{ selectedOwnerPriceNight ? formatPrice(selectedOwnerPriceNight) + ' CFA/nuitée' : '—' }}</span>
              &nbsp;·&nbsp;
              <span class="font-medium">{{ selectedOwnerPriceMonth ? formatPrice(selectedOwnerPriceMonth) + ' CFA/mois' : '—' }}</span>
            </p>
            <p class="text-sm text-gray-500">{{ selectedOwnerPhone }}</p>
          </div>
          <button
            type="button"
            @click="closeContactPopup"
            class="text-gray-400 hover:text-gray-600"
            aria-label="Fermer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <div class="grid grid-cols-1 gap-3">
          <a
            :href="callLink"
            class="w-full inline-flex items-center justify-center rounded-lg bg-[#D4AF37] hover:bg-[#C9A227] text-white py-2.5 font-medium transition-colors"
          >
            Appeler
          </a>
          <a
            :href="whatsappLink"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full inline-flex items-center justify-center rounded-lg bg-green-600 hover:bg-green-700 text-white py-2.5 font-medium transition-colors"
          >
            Contacter sur WhatsApp
          </a>
        </div>
      </div>
    </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { debounce } from 'lodash'
import apiClient from '../../services/api'
import { useRouter } from 'vue-router'
import HeaderComponent from '@/components/HeaderComponentAdmin.vue'
import SideBarComponent from '@/components/SideBarComponent.vue'
import { useAuthStore } from '@/store/auth';
import { toast } from 'vue3-toastify'

const authStore = useAuthStore();

const router = useRouter()
const openIndex = ref(null)
const newBien = ref(false)
const currentPage = ref(1)
const limit = ref(10)
const totalItems = ref(0)
const filters = ref({
  type: '',
  locality: '',
  budget: '',
  rooms: '',
  keyword: ''
})

const apartments = ref([])
const isContactPopupOpen = ref(false)
const selectedOwnerPriceNight = ref(null)
const selectedOwnerPriceMonth = ref(null)
const selectedOwnerPhone = ref('')

// Calcul du nombre total de pages
const totalPages = computed(() => Math.ceil(totalItems.value / limit.value))

// Pagination visible (avec ellipsis)
const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  
  if (totalPages.value <= maxVisible) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i)
    }
  } else {
    const startPage = Math.max(1, currentPage.value - 2)
    const endPage = Math.min(totalPages.value, currentPage.value + 2)
    
    if (startPage > 1) pages.push(1)
    if (startPage > 2) pages.push('...')
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    
    if (endPage < totalPages.value - 1) pages.push('...')
    if (endPage < totalPages.value) pages.push(totalPages.value)
  }
  
  return pages
})

// Fonction de récupération des appartements
const fetchApartments = async () => {
  try {
    const params = {
      page: currentPage.value,
      limit: limit.value,
      ...filters.value
    }
    
    // Nettoyer les filtres vides
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null) delete params[key]
    })

    const { data, headers } = await apiClient.get('/apartments/admin', { params });
    apartments.value = data.data; // Si vous adoptez la structure recommandée
    totalItems.value = data.pagination.total; // Ou headers['x-total-count']
  } catch (error) {
    toast.error('Erreur lors de la récupération des appartements.', {
      position: 'top-right',
      autoClose: 3000
    });
  }
}

// Debounce pour les champs de recherche
const debouncedFetch = debounce(fetchApartments, 500)

// Navigation entre les pages
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchApartments()
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchApartments()
  }
}

const goToPage = (page) => {
  if (page !== '...' && page !== currentPage.value) {
    currentPage.value = page
    fetchApartments()
  }
}

watch(currentPage, () => {
  fetchApartments()
})


// Fonctions utilitaires
const goToAddBienPage = () => {
  if (!authStore.canManageApartments) {
    toast.error('Accès réservé aux administrateurs.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  router.push('/admin/apartments/add')
}

const toggleDropdown = (index) => {
  openIndex.value = openIndex.value === index ? null : index
}

const labelDisponibilite = (value) => {
  return {
    available: 'Disponible',
    coming_soon: 'Bientôt dispo',
    unavailable: 'Indisponible'
  }[value] || value
}

const getApartmentImage = (apartment) => {
  const image = apartment?.images?.[0]
  return image ? `https://api.mohimmo.com/uploads/${image}` : 'https://placehold.co/600x400?text=No+Image'
}

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '0'
  return Number(price).toLocaleString('fr-FR')
}

const isFurnished = (apartment) => {
  const furnishedFeatures = [
    apartment?.wifi,
    apartment?.security,
    apartment?.waterHeater,
    apartment?.terrace,
    apartment?.privateEntrance,
    apartment?.smokerFriendly,
    apartment?.tv,
    apartment?.jardin,
    apartment?.piscine,
    apartment?.generator,
    apartment?.washingMachine,
    apartment?.iron,
    apartment?.airConditioner,
    apartment?.fan,
    apartment?.extra?.microwave,
    apartment?.extra?.canal,
    apartment?.extra?.netflix
  ]

  return furnishedFeatures.some(Boolean)
}

const editApartment = (apt) => {
  if (!authStore.canManageApartments) {
    toast.error('Accès réservé aux administrateurs.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  router.push(`/admin/apartments/edit/${apt._id}`)
}

const sanitizePhone = (phone) => {
  if (!phone) return ''
  return String(phone).replace(/[^\d+]/g, '')
}

const sanitizePhoneForWhatsApp = (phone) => {
  if (!phone) return ''
  return String(phone).replace(/\D/g, '')
}

const callLink = computed(() => {
  const sanitized = sanitizePhone(selectedOwnerPhone.value)
  return sanitized ? `tel:${sanitized}` : '#'
})

const whatsappLink = computed(() => {
  const sanitized = sanitizePhoneForWhatsApp(selectedOwnerPhone.value)
  return sanitized ? `https://wa.me/${sanitized}` : '#'
})

const openContactPopup = (apartment) => {
  if (!authStore.canViewOwnerInfo) {
    toast.error('Accès réservé au service client et aux super admins.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  selectedOwnerPriceNight.value = apartment.ownerPriceNight || null
  selectedOwnerPriceMonth.value = apartment.ownerPriceMonth || null
  selectedOwnerPhone.value = apartment.ownerPhone || ''
  isContactPopupOpen.value = true
}

const closeContactPopup = () => {
  isContactPopupOpen.value = false
}

const toggleAvailability = async (apt) => {
  if (!authStore.canManageApartments) {
    toast.error('Accès réservé aux administrateurs.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    const options = ['available', 'coming_soon', 'unavailable']
    const next = options[(options.indexOf(apt.availability) + 1) % options.length]
    await apiClient.patch(`/apartments/${apt._id}/availability`, { availability: next })
    fetchApartments()
  } catch (error) {
    toast.error('Erreur lors du changement de statut.', {
      position: 'top-right',
      autoClose: 3000
    });
  }
}

const shareApartment = async (apartment) => {
  const shareUrl = `${window.location.origin}/apartments/${apartment._id}`
  const shareText = `Découvrez ce bien à ${apartment.locality}: ${apartment.title}`

  try {
    if (navigator.share) {
      await navigator.share({
        title: apartment.title,
        text: shareText,
        url: shareUrl
      })
      return
    }

    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.open(`whatsapp://send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank')
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
      toast.success('Lien copié dans le presse-papier !', {
        position: 'top-right',
        autoClose: 3000
      })
    } else {
      copyToClipboardFallback(`${shareText}\n${shareUrl}`)
      toast.success('Lien copié dans le presse-papier !', {
        position: 'top-right',
        autoClose: 3000
      })
    }
  } catch (err) {
    console.error('Erreur de partage:', err)
    toast.error('Échec du partage', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

const copyToClipboardFallback = (text) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const deleteApartment = async (id) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer cet appartement ? Cette action est irréversible.')) {
    try {
      await apiClient.delete(`/apartments/${id}`)
      fetchApartments()
    } catch (error) {
      toast.error('Erreur lors de la suppression.', {
        position: 'top-right',
        autoClose: 3000
      });
    }
  }
}

// Watchers et lifecycle hooks
watch(filters.value, () => {
  currentPage.value = 1
  debouncedFetch()
})

onMounted(fetchApartments)
</script>
